import { css } from 'astroturf';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FlexCheatsheet } from './flex-cheatsheet';
import { GridCheatsheet } from './grid-cheatsheet';
import { AnchorCheatsheet } from './anchor-cheatsheet';
import { InvokerCheatsheet } from './invoker-cheatsheet';
import Brow from '../Brow';

import flexPDF from '../../../assets/images/cheatsheets/flex.pdf';
import flexPNG from '../../../assets/images/cheatsheets/flex.png';
import gridPDF from '../../../assets/images/cheatsheets/grid.pdf';
import gridPNG from '../../../assets/images/cheatsheets/grid.png';
import anchorPDF from '../../../assets/images/cheatsheets/anchor.pdf';
import anchorPNG from '../../../assets/images/cheatsheets/anchor.png';
import invokerPDF from '../../../assets/images/cheatsheets/invoker.pdf';
import invokerPNG from '../../../assets/images/cheatsheets/invoker.png';

const styles = css`
  .pageRoot {
    --background: oklch(0.12 0.02 260);
    --foreground: oklch(0.95 0 0);
    --card: oklch(0.16 0.02 260);
    --card-foreground: oklch(0.95 0 0);
    --popover: oklch(0.16 0.02 260);
    --popover-foreground: oklch(0.95 0 0);
    --primary: oklch(0.7 0.18 200);
    --primary-foreground: oklch(0.12 0.02 260);
    --secondary: oklch(0.22 0.02 260);
    --secondary-foreground: oklch(0.95 0 0);
    --muted: oklch(0.22 0.02 260);
    --muted-foreground: oklch(0.65 0 0);
    --accent: oklch(0.75 0.15 150);
    --accent-foreground: oklch(0.12 0.02 260);
    --destructive: oklch(0.577 0.245 27.325);
    --destructive-foreground: oklch(0.577 0.245 27.325);
    --border: oklch(0.28 0.02 260);
    --input: oklch(0.22 0.02 260);
    --ring: oklch(0.7 0.18 200);
    --chart-1: oklch(0.7 0.18 200);
    --chart-2: oklch(0.75 0.15 150);
    --chart-3: oklch(0.8 0.18 80);
    --chart-4: oklch(0.65 0.2 320);
    --chart-5: oklch(0.7 0.2 30);
    --radius: 0.625rem;
    --sidebar: oklch(0.14 0.02 260);
    --sidebar-foreground: oklch(0.95 0 0);
    --sidebar-primary: oklch(0.7 0.18 200);
    --sidebar-primary-foreground: oklch(0.12 0.02 260);
    --sidebar-accent: oklch(0.22 0.02 260);
    --sidebar-accent-foreground: oklch(0.95 0 0);
    --sidebar-border: oklch(0.28 0.02 260);
    --sidebar-ring: oklch(0.7 0.18 200);

    --font-sans: 'Fira Code', 'Fira Code Fallback', monospace;
    --font-mono: 'Fira Code', 'Fira Code Fallback', monospace;

    --color-background: var(--background);
    --color-foreground: var(--foreground);
    --color-card: var(--card);
    --color-card-foreground: var(--card-foreground);
    --color-popover: var(--popover);
    --color-popover-foreground: var(--popover-foreground);
    --color-primary: var(--primary);
    --color-primary-foreground: var(--primary-foreground);
    --color-secondary: var(--secondary);
    --color-secondary-foreground: var(--secondary-foreground);
    --color-muted: var(--muted);
    --color-muted-foreground: var(--muted-foreground);
    --color-accent: var(--accent);
    --color-accent-foreground: var(--accent-foreground);
    --color-destructive: var(--destructive);
    --color-destructive-foreground: var(--destructive-foreground);
    --color-border: var(--border);
    --color-input: var(--input);
    --color-ring: var(--ring);
    --color-chart-1: var(--chart-1);
    --color-chart-2: var(--chart-2);
    --color-chart-3: var(--chart-3);
    --color-chart-4: var(--chart-4);
    --color-chart-5: var(--chart-5);
    --radius-sm: calc(var(--radius) - 4px);
    --radius-md: calc(var(--radius) - 2px);
    --radius-lg: var(--radius);
    --radius-xl: calc(var(--radius) + 4px);
    --color-sidebar: var(--sidebar);
    --color-sidebar-foreground: var(--sidebar-foreground);
    --color-sidebar-primary: var(--sidebar-primary);
    --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
    --color-sidebar-accent: var(--sidebar-accent);
    --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
    --color-sidebar-border: var(--sidebar-border);
    --color-sidebar-ring: var(--sidebar-ring);

    /* Cheatsheet palette tokens used by CSS modules */
    --sheet-slate-900: #0f172a;
    --sheet-slate-700: #334155;
    --sheet-slate-600: #475569;
    --sheet-slate-500: #64748b;
    --sheet-slate-400: #94a3b8;
    --sheet-slate-300: #cbd5e1;
    --sheet-slate-200: #e2e8f0;
    --sheet-cyan-500: #06b6d4;
    --sheet-cyan-400: #22d3ee;
    --sheet-cyan-300: #67e8f9;
    --sheet-cyan-100: #cffafe;
    --sheet-emerald-500: #10b981;
    --sheet-emerald-400: #34d399;
    --sheet-emerald-300: #6ee7b7;

    --sheet-slate-900-50: rgba(15, 23, 42, 0.5);
    --sheet-slate-700-50: rgba(51, 65, 85, 0.5);
    --sheet-slate-600-50: rgba(71, 85, 105, 0.5);
    --sheet-slate-600-40: rgba(71, 85, 105, 0.4);
    --sheet-slate-600-30: rgba(71, 85, 105, 0.3);
    --sheet-slate-500-50: rgba(100, 116, 139, 0.5);
    --sheet-cyan-500-80: rgba(6, 182, 212, 0.8);
    --sheet-cyan-500-70: rgba(6, 182, 212, 0.7);
    --sheet-cyan-500-60: rgba(6, 182, 212, 0.6);
    --sheet-cyan-500-30: rgba(6, 182, 212, 0.3);
    --sheet-cyan-400-20: rgba(34, 211, 238, 0.2);
    --sheet-cyan-300-30: rgba(103, 232, 249, 0.3);
    --sheet-emerald-500-80: rgba(16, 185, 129, 0.8);
    --sheet-emerald-400-60: rgba(52, 211, 153, 0.6);
    --sheet-ink-92: rgba(2, 6, 23, 0.92);
    --sheet-ink-88: rgba(2, 6, 23, 0.88);
    --sheet-ink-28: rgba(2, 6, 23, 0.28);

    /* Preflight-style normalization (Tailwind-like, plain CSS) */

    hr {
      height: 0;
      color: inherit;
      border-top-width: 1px;
    }

    abbr:where([title]) {
      text-decoration: underline dotted;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-size: inherit;
      font-weight: inherit;
    }

    b,
    strong {
      font-weight: bolder;
    }

    code,
    kbd,
    samp,
    pre {
      font-family: var(--font-mono);
      font-size: 1em;
    }

    small {
      font-size: 80%;
    }

    sub,
    sup {
      font-size: 75%;
      line-height: 0;
      position: relative;
      vertical-align: baseline;
    }

    sub {
      bottom: -0.25em;
    }

    sup {
      top: -0.5em;
    }

    table {
      text-indent: 0;
      border-color: inherit;
      border-collapse: collapse;
    }

    button,
    input,
    optgroup,
    select,
    textarea {
      font: inherit;
      font-feature-settings: inherit;
      font-variation-settings: inherit;
      letter-spacing: inherit;
      color: inherit;
      margin: 0;
      padding: 0;
      border-radius: 0;
      background-color: transparent;
      opacity: 1;
    }

    button,
    select {
      text-transform: none;
    }

    button,
    input:where([type='button'], [type='reset'], [type='submit']) {
      -webkit-appearance: button;
      background-image: none;
    }

    :-moz-focusring {
      outline: auto;
    }

    :-moz-ui-invalid {
      box-shadow: none;
    }

    progress {
      vertical-align: baseline;
    }

    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      height: auto;
    }

    [type='search'] {
      -webkit-appearance: textfield;
      outline-offset: -2px;
    }

    ::-webkit-search-decoration {
      -webkit-appearance: none;
    }

    ::-webkit-file-upload-button {
      -webkit-appearance: button;
      font: inherit;
    }

    summary {
      display: list-item;
    }

    blockquote,
    dl,
    dd,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    hr,
    figure,
    p,
    pre {
      margin: 0;
    }

    fieldset {
      margin: 0;
      padding: 0;
    }

    legend {
      padding: 0;
    }

    ol,
    ul,
    menu {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    dialog {
      padding: 0;
    }

    textarea {
      resize: vertical;
    }

    input::placeholder,
    textarea::placeholder {
      opacity: 1;
      color: color-mix(in oklab, var(--foreground) 60%, transparent);
    }

    [role='button'] {
      cursor: pointer;

      &:focus-visible {
        outline-offset: 5px;
        border-radius: 15px;
        outline: 15px solid #06b6d4;
      }
    }

    :disabled {
      cursor: default;
    }

    img,
    svg,
    video,
    canvas,
    audio,
    iframe,
    embed,
    object {
      display: block;
      vertical-align: middle;
    }

    img,
    video {
      max-width: 100%;
      height: auto;
    }

    [hidden]:where(:not([hidden='until-found'])) {
      display: none;
    }

    *:focus-visible {
      outline-color: color-mix(in oklab, var(--ring) 50%, transparent);
    }

    article {
      background: linear-gradient(145deg, rgba(0, 17, 34, 0.733), rgba(0, 17, 34, 0.4), rgba(0, 17, 34, 0.733));
      box-shadow: rgba(255, 255, 255, 0.25) 0px 1px 1px 0px inset;
      > div {
        box-shadow:
          rgba(255, 255, 255, 0.6) 0px 1px 1px 0px inset,
          rgba(50, 50, 93, 0.7) 0px 5px 10px;
      }
    }

    .twoColumn {
      width: 530px !important;

      > div {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0 1rem;
      }
    }

    .topDownFourRows {
      > div {
        grid-auto-flow: column;
        grid-template-rows: repeat(4, auto);
      }
    }

    .topDownThreeRows {
      > div {
        grid-auto-flow: column;
        grid-template-rows: repeat(3, auto);
      }
    }

    .topDownOneRow {
      > div {
        grid-auto-flow: column;
        grid-template-rows: repeat(1, auto);
      }
    }

    .wideColumn {
      width: 530px !important;
    }

    section h2 {
      background: linear-gradient(to right, lab(75.0771 -60.7313 19.4147 / 0.33), lab(75.0771 -60.7313 19.4147 / 0));
      border-radius: 4px;
      padding-left: 4px !important;
      margin-left: -4px !important;
    }

    p {
      font-family: var(--font-inter);
    }
  }

  .pageRoot {
    position: relative;
    min-height: 100vh;
    overflow-x: hidden;
  }

  .viewport {
    position: relative;
    min-height: 92svh;
    width: 100%;
  }

  .deckStage {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    margin-left: auto;
    margin-right: auto;
    height: 56svh;
    width: 100%;
    max-width: 1280px;
    perspective: 2200px;
  }

  .cardTooltip {
    pointer-events: none;
    position: absolute;
    left: 50%;
    white-space: nowrap;
    border-radius: 9999px;
    border: 1px solid var(--sheet-cyan-400-20);
    background-color: var(--sheet-ink-92);
    padding: 0.25rem 0.75rem;
    font-size: 11px;
    font-weight: 600;
    color: var(--sheet-cyan-100);
    backdrop-filter: blur(12px);
  }

  .cardShell {
    position: absolute;
    left: 50%;
    user-select: none;
    outline: none;
  }

  .cursorDefault {
    cursor: default;
  }

  .cursorPointer {
    cursor: pointer;
  }

  .controlsRow {
    position: absolute;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .downloadButton {
    white-space: nowrap;
    border-radius: 9999px;
    border: 5px solid var(--sheet-cyan-300-30);
    background-color: var(--sheet-ink-88);
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--sheet-cyan-100);
    box-shadow: 0 4px 14px var(--sheet-ink-28);
    backdrop-filter: blur(12px);
    transition:
      filter 220ms ease-in-out,
      scale 220ms ease-in-out;
    display: flex;
    gap: 0.5rem;
  }

  .downloadButton:hover {
    filter: brightness(1.08);
    scale: 1.02;
  }

  .content {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    flex-direction: column;
    max-width: 540px;
    margin: auto;
    translate: 0 -8svh;

    div {
      display: flex;
      flex-direction: column;
      text-align: center;

      h1 {
        font-size: 40px;
        font-weight: 900;
      }

      p {
        margin-bottom: 0.5rem;
        text-wrap: balance;
      }
    }
  }
`;

const cheatsheets = [
  {
    id: 'flex',
    title: 'CSS Flexbox',
    hash: '#flex',
    pdfUrl: flexPDF,
    pngUrl: flexPNG,
    Component: FlexCheatsheet,
  },
  {
    id: 'grid',
    title: 'CSS Grid',
    hash: '#grid',
    pdfUrl: gridPDF,
    pngUrl: gridPNG,
    Component: GridCheatsheet,
  },
  {
    id: 'anchor',
    title: 'Anchor Positioning',
    hash: '#anchor',
    pdfUrl: anchorPDF,
    pngUrl: anchorPNG,
    Component: AnchorCheatsheet,
  },
  {
    id: 'invoker',
    title: 'Invoker Commands',
    hash: '#invoker',
    pdfUrl: invokerPDF,
    pngUrl: invokerPNG,
    Component: InvokerCheatsheet,
  },
];

const CARD_BASE_WIDTH_PX = (148 / 25.4) * 96;
const CARD_BASE_HEIGHT_PX = (210 / 25.4) * 96;
const CARD_TRANSITION_MS = 2000;
const CONTROLS_APPEAR_MS = 600;
const CONTROLS_EXIT_MS = 100;

function parseHashToCheatsheetId(hash) {
  const normalized = hash.replace('#', '');
  return cheatsheets.some((item) => item.id === normalized) ? normalized : null;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function CheatsheetPage() {
  const [withBleed] = useState(false);
  const [activeCheatsheetId, setActiveCheatsheetId] = useState(null);
  const [hoveredCheatsheetId, setHoveredCheatsheetId] = useState(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [returningCardId, setReturningCardId] = useState(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const exitTimerRef = useRef(null);
  const returnTimerRef = useRef(null);
  const viewportRef = useRef(null);
  const [tiltXY, setTiltXY] = useState({ x: 0, y: 0 });
  const [isTilting, setIsTilting] = useState(false);
  const [glareXY, setGlareXY] = useState({ x: 50, y: 50 });
  const rafRef = useRef(null);
  const [prefersReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const syncFromHash = () => {
      setActiveCheatsheetId(parseHashToCheatsheetId(window.location.hash));
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  useEffect(() => {
    const bounds = viewportRef.current?.getBoundingClientRect();
    const syncViewport = () =>
      setViewport({ width: bounds?.width || window.innerWidth, height: bounds?.height || window.innerHeight });
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
      if (returnTimerRef.current) window.clearTimeout(returnTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!activeCheatsheetId) {
      setControlsVisible(false);
      return;
    }
    setControlsVisible(false);
    const timer = window.setTimeout(() => setControlsVisible(true), CONTROLS_APPEAR_MS);
    return () => window.clearTimeout(timer);
  }, [activeCheatsheetId]);

  useEffect(() => {
    setTiltXY({ x: 0, y: 0 });
    setIsTilting(false);
  }, [activeCheatsheetId]);

  const selectCheatsheet = (nextId) => {
    if (activeCheatsheetId === nextId) return;
    if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
    if (returnTimerRef.current) window.clearTimeout(returnTimerRef.current);
    setControlsVisible(false);
    if (activeCheatsheetId) {
      const leavingId = activeCheatsheetId;
      setReturningCardId(leavingId);
      exitTimerRef.current = window.setTimeout(() => {
        setActiveCheatsheetId(nextId);
        window.history.replaceState(null, '', `#${nextId}`);
      }, CONTROLS_EXIT_MS);
      returnTimerRef.current = window.setTimeout(
        () => {
          setReturningCardId((cur) => (cur === leavingId ? null : cur));
        },
        CONTROLS_EXIT_MS + CARD_TRANSITION_MS + 200
      );
    } else {
      setActiveCheatsheetId(nextId);
      window.history.replaceState(null, '', `#${nextId}`);
    }
  };

  const clearSelection = () => {
    if (!activeCheatsheetId) return;
    if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
    if (returnTimerRef.current) window.clearTimeout(returnTimerRef.current);
    const leavingId = activeCheatsheetId;
    setReturningCardId(leavingId);
    setControlsVisible(false);
    exitTimerRef.current = window.setTimeout(() => {
      setActiveCheatsheetId(null);
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }, CONTROLS_EXIT_MS);
    returnTimerRef.current = window.setTimeout(
      () => {
        setReturningCardId((cur) => (cur === leavingId ? null : cur));
      },
      CONTROLS_EXIT_MS + CARD_TRANSITION_MS + 200
    );
  };

  const handleBackgroundPointerDown = (event) => {
    if (!activeCheatsheetId) return;
    const target = event.target;
    if (!target.closest("[data-cheatsheet-card='true']")) {
      clearSelection();
    }
  };

  const activeIndex = cheatsheets.findIndex((item) => item.id === activeCheatsheetId);

  const viewportAspectRatio = useMemo(() => {
    if (!viewport.width || !viewport.height) return 1;
    return viewport.width / viewport.height;
  }, [viewport.height, viewport.width]);

  const miniScale = useMemo(() => {
    if (!viewport.width || !viewport.height) return 0.16;
    const spreadRatio = (viewport.width - 24) / (cheatsheets.length * CARD_BASE_WIDTH_PX * 0.64);
    const heightRatio = (viewport.height * 0.19) / CARD_BASE_HEIGHT_PX;
    return clamp(Math.min(spreadRatio, heightRatio), 0.1, 0.2);
  }, [viewport.height, viewport.width]);

  const fanSpacing = useMemo(() => {
    if (!viewport.width) return 112;
    return clamp(viewport.width / (cheatsheets.length + 4.4), 44, 98);
  }, [viewport.width]);

  const safeBottomInset = useMemo(() => {
    if (!viewport.height) return 42;
    const ratioCompensation = viewportAspectRatio < 0.8 ? 48 : 44;
    return clamp(viewport.height * 0.1 + ratioCompensation, 44, 100);
  }, [viewport.height, viewportAspectRatio]);

  const fanRotateFactor = viewportAspectRatio < 0.8 ? 4.2 : 5.4;

  const focusScale = useMemo(() => {
    if (!viewport.width || !viewport.height) return 0.92;
    const maxWidth = viewport.width * 0.88;
    const maxHeight = viewport.height * 0.69;
    return Math.min(maxWidth / CARD_BASE_WIDTH_PX, maxHeight / CARD_BASE_HEIGHT_PX, 1.3);
  }, [viewport.height, viewport.width]);

  const focusCenterY = useMemo(() => {
    if (!viewport.height) return 0;
    return viewport.height * 0.375;
  }, [viewport.height, viewportAspectRatio]);

  const focusTranslateY = useMemo(() => {
    if (!viewport.height) return -220;
    const focusedHeight = CARD_BASE_HEIGHT_PX * focusScale;
    return focusCenterY - viewport.height + safeBottomInset + focusedHeight / 2;
  }, [focusCenterY, focusScale, safeBottomInset, viewport.height]);

  return (
    <main className={styles.pageRoot} onPointerDown={handleBackgroundPointerDown}>
      <div className={styles.content}>
        <div>
          <Brow>Polypane presents</Brow>
          <h1>Cheatsheets</h1>
          <p>
            A collection of handy sheets for web devs, covering CSS Flexbox, CSS Grid, Anchor Positioning, and Invoker
            Commands.
          </p>
          <p>Click a cheatsheet to see it and download it as a PDF or PNG.</p>
          <p><i>Free and print-ready (a5 format with 3mm bleed).</i></p>
        </div>
      </div>

      <div id="viewport" className={styles.viewport} ref={viewportRef}>
        <section className={styles.deckStage}>
          {cheatsheets.map(({ id, title, Component, hash, pdfUrl, pngUrl }, index) => {
            const centerOffset = index - (cheatsheets.length - 1) / 2;
            const baseX = centerOffset * fanSpacing * 0.82;
            const baseRotate = centerOffset * fanRotateFactor;
            const fanDrop = Math.pow(Math.abs(centerOffset), 1.06) * 6.6;
            const isActive = activeCheatsheetId === id;
            const isReturning = returningCardId === id;
            const isHovered = hoveredCheatsheetId === id;

            let scootX = 0;
            if (activeIndex !== -1 && !isActive) {
              const directionToCenter = activeIndex > index ? 1 : -1;
              scootX = directionToCenter * clamp(fanSpacing * 0.33, 16, 44);
            }

            const cardScale = miniScale * (isHovered ? 1.08 : 1);
            const cardY = fanDrop - (isHovered ? 14 : 0) + 18;
            const isTooltipVisible = isHovered && !isActive;
            const tiltRotateY = isActive && !prefersReducedMotion ? -tiltXY.x * 6 : 0;
            const tiltRotateX = isActive && !prefersReducedMotion ? tiltXY.y * 6 : 0;
            const shadowOffX = isActive ? tiltXY.x * 18 : 0;
            const shadowOffY = isActive ? 14 - tiltXY.y * 12 : 14;
            const shadowAlpha = isActive ? 0.18 + Math.sqrt(tiltXY.x ** 2 + tiltXY.y ** 2) * 0.12 : 0.18;

            const originFactorY = 1.14;
            const angleRad = (baseRotate * Math.PI) / 180;
            const originToTop = CARD_BASE_HEIGHT_PX * cardScale * originFactorY;
            const projectedTopMidX = Math.sin(angleRad) * originToTop;
            const projectedTopHeight =
              Math.cos(angleRad) * originToTop - CARD_BASE_HEIGHT_PX * cardScale * (originFactorY - 1);
            const tooltipBottom = safeBottomInset + cardY + projectedTopHeight - 45;
            const tooltipX = baseX + scootX + projectedTopMidX;

            return (
              <Fragment key={id}>
                <span
                  aria-hidden="true"
                  className={styles.cardTooltip}
                  style={{
                    bottom: `${tooltipBottom}px`,
                    transform: `perspective(300px) translateX(calc(-50% + ${tooltipX}px)) translateY(${isTooltipVisible ? '45px' : '150px'}) scale(${isTooltipVisible ? 1 : 0.82})`,
                    transformOrigin: '50% 100%',
                    opacity: isTooltipVisible ? 1 : 0,
                    filter: isTooltipVisible ? 'blur(0px)' : 'blur(10px)',
                    boxShadow: '0 2px 8px rgba(2, 6, 23, 0.26)',
                    transition: 'opacity 420ms ease, transform 520ms ease, filter 460ms ease',
                  }}
                >
                  {title}
                </span>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={() => selectCheatsheet(id)}
                  onMouseEnter={() => setHoveredCheatsheetId(id)}
                  onMouseLeave={() => setHoveredCheatsheetId((cur) => (cur === id ? null : cur))}
                  onFocus={() => setHoveredCheatsheetId(id)}
                  onBlur={() => setHoveredCheatsheetId((cur) => (cur === id ? null : cur))}
                  onPointerMove={(e) => {
                    if (!isActive || e.pointerType !== 'mouse' || prefersReducedMotion) return;
                    if (e.target.closest('[data-cheatsheet-controls="true"]')) {
                      setTiltXY({ x: 0, y: 0 });
                      setGlareXY({ x: 50, y: 50 });
                      setIsTilting(false);
                      return;
                    }
                    const rect = e.currentTarget.getBoundingClientRect();
                    const nx = clamp((e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2), -1, 1);
                    const ny = clamp((e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2), -1, 1);
                    if (rafRef.current) cancelAnimationFrame(rafRef.current);
                    rafRef.current = requestAnimationFrame(() => {
                      setTiltXY({ x: nx, y: ny });
                      setGlareXY({ x: nx * 50 + 50, y: ny * 50 + 50 });
                      if (!isTilting) setIsTilting(true);
                    });
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType !== 'mouse') return;
                    if (rafRef.current) cancelAnimationFrame(rafRef.current);
                    setTiltXY({ x: 0, y: 0 });
                    setIsTilting(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectCheatsheet(id);
                    }
                  }}
                  role={isActive ? 'generic' : 'button'}
                  {...(isActive ? {} : { 'aria-label': `Open ${title}`, tabIndex: 0 })}
                  aria-current={isActive}
                  data-cheatsheet-id={id}
                  data-cheatsheet-hash={hash}
                  data-cheatsheet-card="true"
                  className={`${styles.cardShell} ${isActive ? styles.cursorDefault : styles.cursorPointer}`}
                  style={{
                    bottom: `${safeBottomInset}px`,
                    translate: isActive ? `-50% ${focusTranslateY}px` : `calc(-50% + ${baseX + scootX}px) ${cardY}px`,
                    rotate: isActive ? `0deg` : `${baseRotate}deg`,
                    scale: isActive ? `${focusScale}` : `${cardScale}`,
                    transform: `rotateY(${isActive ? 360 : isReturning ? 360 : 0}deg)`,
                    transformOrigin: '50% 114%',
                    willChange: 'transform',
                    touchAction: 'manipulation',
                    transition: isActive
                      ? `translate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), rotate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), scale ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), filter ${isTilting ? 60 : 800}ms ease-in-out`
                      : isReturning
                        ? `translate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), rotate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), scale ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform 0ms, filter 60ms ease-in-out`
                        : `translate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), rotate ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), scale ${CARD_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), transform 0ms, filter 60ms ease-in-out`,
                    filter: isActive
                      ? `drop-shadow(${shadowOffX.toFixed(1)}px ${shadowOffY.toFixed(1)}px 30px rgba(2,6,23,${shadowAlpha.toFixed(2)}))`
                      : isHovered
                        ? 'drop-shadow(0 6px 10px rgba(2,6,23,0.2))'
                        : 'drop-shadow(0 3px 7px rgba(2,6,23,0.16))',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      transform: `perspective(700px) scale(0.97) rotateX(${tiltRotateX}deg) rotateY(${tiltRotateY}deg)`,
                      transition:
                        isTilting && isActive
                          ? 'transform 60ms linear'
                          : 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1)',
                      willChange: isActive ? 'transform' : 'auto',
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        zIndex: 2,
                      }}
                    >
                      <Component withBleed={withBleed} />
                    </div>
                    {isActive && !prefersReducedMotion && (
                      <div
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          zIndex: 3,
                          background: `radial-gradient(circle at ${glareXY.x.toFixed(1)}% ${glareXY.y.toFixed(1)}%, rgba(103,232,249,0.24) 0%, rgba(103,232,249,0.07) 40%, transparent 65%)`,
                          opacity: isTilting ? 1 : 0,
                          transition: 'opacity 400ms ease-in-out',
                        }}
                      />
                    )}
                  </div>

                  {isActive || isReturning ? (
                    <div
                      data-cheatsheet-controls="true"
                      className={styles.controlsRow}
                      style={{
                        top: 'calc(100% + 12px)',
                        zIndex: 1,
                        opacity: isActive && controlsVisible ? 1 : 0,
                        transform: `translateX(-50%) translateY(${isActive && controlsVisible ? '0px' : '-200px'}) scale(${isActive && controlsVisible ? 1 : 0.88})`,
                        transition:
                          isActive && controlsVisible
                            ? 'opacity 280ms ease-in-out, transform 680ms cubic-bezier(0.16, 1, 0.3, 1), scale 680ms cubic-bezier(0.16, 1, 0.3, 1)'
                            : 'opacity 560ms ease-in-out, transform 600ms cubic-bezier(0.16, 1, 0.3, 1), scale 600ms cubic-bezier(0.16, 1, 0.3, 1)',
                        pointerEvents: isActive && controlsVisible ? 'auto' : 'none',
                      }}
                    >
                      <a
                        href={pdfUrl}
                        download={`${title}.pdf`}
                        className={styles.downloadButton}
                        style={{
                          opacity: isActive && controlsVisible ? 1 : 0,
                          transform: `translateY(${isActive && controlsVisible ? '0px' : '-12px'}) scale(${isActive && controlsVisible ? 1 : 0.88})`,
                          transition:
                            isActive && controlsVisible
                              ? 'opacity 380ms ease-in-out, transform 500ms cubic-bezier(0.16, 1, 0.3, 1), filter 400ms ease-in-out, scale 300ms ease-in-out'
                              : 'opacity 600ms ease-in-out, transform 400ms cubic-bezier(0.16, 1, 0.3, 1), filter 640ms ease-in-out, scale 300ms ease-in-out',
                          transitionDelay: isActive && controlsVisible ? '0ms' : '0ms',
                          filter: isActive && controlsVisible ? 'blur(0)' : 'blur(8px)',
                        }}
                      >
                        Download as <PDFIcon />
                      </a>
                      <a
                        href={pngUrl}
                        download={`${title}.png`}
                        className={styles.downloadButton}
                        style={{
                          opacity: isActive && controlsVisible ? 1 : 0,
                          transform: `translateY(${isActive && controlsVisible ? '0px' : '-12px'}) scale(${isActive && controlsVisible ? 1 : 0.88})`,
                          transition:
                            isActive && controlsVisible
                              ? 'opacity 380ms ease-in-out, transform 500ms cubic-bezier(0.16, 1, 0.3, 1), filter 400ms ease-in-out, scale 300ms ease-in-out'
                              : 'opacity 600ms ease-in-out, transform 400ms cubic-bezier(0.16, 1, 0.3, 1), filter 640ms ease-in-out, scale 300ms ease-in-out',
                          transitionDelay: isActive && controlsVisible ? '200ms' : '0ms',
                          filter: isActive && controlsVisible ? 'blur(0)' : 'blur(8px)',
                        }}
                      >
                        Download as <PNGIcon />
                      </a>
                    </div>
                  ) : null}
                </div>
              </Fragment>
            );
          })}
        </section>
      </div>
    </main>
  );
}

const PDFIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-file-type-pdf"
  >
    <title>PDF</title>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
    <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
    <path d="M17 18h2" />
    <path d="M20 15h-3v6" />
    <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1" />
  </svg>
);

const PNGIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-file-type-png"
  >
    <title>PNG</title>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
    <path d="M20 15h-1a2 2 0 0 0 -2 2v2a2 2 0 0 0 2 2h1v-3" />
    <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
    <path d="M11 21v-6l3 6v-6" />
  </svg>
);
